/**
 * @fileoverview Message composer component for sending SMS.
 * Includes character counting, segment calculation, and opt-out handling.
 */

import { useState } from 'react';
import { Send, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * Props for MessageComposer component.
 */
interface MessageComposerProps {
  /** Callback when message is sent */
  onSend: (message: string) => void;
  /** Whether recipient has opted out */
  isOptedOut: boolean;
  /** Whether a send operation is in progress */
  isSending: boolean;
  /** Additional disabled state */
  disabled?: boolean;
}

/** Standard SMS character limit per segment */
const SMS_CHAR_LIMIT = 160;

/**
 * Renders a text input with send button for composing SMS messages.
 * Displays character count, segment count, and opt-out warnings.
 */
export function MessageComposer({ onSend, isOptedOut, isSending, disabled }: MessageComposerProps) {
  const [message, setMessage] = useState('');

  const charCount = message.length;
  const segments = Math.ceil(charCount / SMS_CHAR_LIMIT) || 1;

  const handleSend = () => {
    if (!message.trim() || isOptedOut || isSending) return;
    onSend(message.trim());
    setMessage('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex-shrink-0 bg-background border-t border-border p-4 space-y-3">
      {isOptedOut && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This number has opted out of receiving messages. You cannot send SMS to this contact.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Textarea
            placeholder={isOptedOut ? 'Cannot send - recipient opted out' : 'Type a message...'}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isOptedOut || disabled}
            className="min-h-[80px] resize-none pr-16"
            rows={3}
          />
          <div className="absolute bottom-2 right-2 text-xs text-muted-foreground">
            {charCount}/{SMS_CHAR_LIMIT} · {segments} {segments === 1 ? 'segment' : 'segments'}
          </div>
        </div>
        <Button
          onClick={handleSend}
          disabled={!message.trim() || isOptedOut || isSending || disabled}
          size="icon"
          className="h-[80px] w-12"
        >
          <Send className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}
